import type { ReactElement } from 'react'
import { SpecialZoomLevel, Viewer, Worker } from '@react-pdf-viewer/core'
import MuiBox from '@mui/material/Box'
import '@react-pdf-viewer/core/lib/styles/index.css'
import type { PdfParams } from '../../../../hooks/usePdf'

/** PDF.js workerのURL */
const PDFJS_WORKER_URL = 'https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js' as const

/**
 * PDFの特殊ズームレベル（@react-pdf-viewer/coreのSpecialZoomLevelに対応）
 *
 * - 'pageFit': ページ全体がビュー内に収まるように表示（デフォルト）
 * - 'pageWidth': ページ幅がビュー幅に合うように表示（横幅いっぱいに表示）
 * - 'actualSize': 実際のPDFサイズで表示（100%表示）
 */
export type PdfSpecialZoomLevel = 'pageFit' | 'pageWidth' | 'actualSize'

/** HTTPヘッダーの型 */
type HttpHeaders = Readonly<Record<string, string>>

/** 共通プロパティ */
interface PdfViewerBaseProps {
  readonly url: string
  readonly height?: number | string
  readonly width?: number | string
  readonly httpHeaders: HttpHeaders
  readonly params: PdfParams
}

/**
 * 特殊ズームレベルモード（デフォルト）
 * defaultZoomLevelで表示モードを指定
 */
interface PdfViewerZoomLevelProps extends PdfViewerBaseProps {
  /**
   * 特殊ズームレベル（SpecialZoomLevel）
   * - 'pageFit': ページ全体を表示（デフォルト）
   * - 'pageWidth': 横幅に合わせて表示（SP版で横幅いっぱいに表示したい場合に使用）
   * - 'actualSize': 実寸表示
   */
  readonly defaultZoomLevel?: PdfSpecialZoomLevel
  /** scaleとdefaultZoomLevelは同時に指定できない */
  readonly scale?: never
}

/**
 * 数値スケールモード
 * scaleで倍率を直接指定
 */
interface PdfViewerScaleProps extends PdfViewerBaseProps {
  /** 数値スケール（倍率）を直接指定（例: 1.5 = 150%） */
  readonly scale: number
  /** scaleとdefaultZoomLevelは同時に指定できない */
  readonly defaultZoomLevel?: never
}

/** PdfViewerのプロパティ（discriminated union） */
export type PdfViewerProps = PdfViewerZoomLevelProps | PdfViewerScaleProps

/**
 * propsからdefaultScale値を取得
 */
function resolveDefaultScale(props: PdfViewerProps): SpecialZoomLevel | number {
  // scaleが指定されている場合（PdfViewerScaleProps）
  if ('scale' in props && props.scale !== undefined) {
    return props.scale
  }

  // defaultZoomLevelが指定されている場合（PdfViewerZoomLevelProps）
  const zoomLevelProps = props
  switch (zoomLevelProps.defaultZoomLevel) {
    case 'pageWidth':
      return SpecialZoomLevel.PageWidth
    case 'actualSize':
      return SpecialZoomLevel.ActualSize
    case 'pageFit':
    default:
      return SpecialZoomLevel.PageFit
  }
}

export default function PdfViewer(props: PdfViewerProps): ReactElement {
  const { url, height, width = '100%', httpHeaders, params } = props
  const plugins = [params.downloadPlugin, params.printPlugin]
  const resolvedScale = resolveDefaultScale(props)

  return (
    <Worker workerUrl={PDFJS_WORKER_URL}>
      <MuiBox sx={{ height, width }}>
        <Viewer fileUrl={url} defaultScale={resolvedScale} plugins={plugins} httpHeaders={httpHeaders} />
      </MuiBox>
    </Worker>
  )
}
